import { Suspense, useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { resolveClientOrgId, resolveRole } from '@/utils/authClaims';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { lazyWithReload as lazy } from '@/utils/lazyWithReload';
import { ToastContainer } from '@/components/common/Toast';
import Layout from '@/components/layout/Layout';
import PortalLayout from '@/components/layout/PortalLayout';

import { useSessionTimeout } from '@/hooks/useSessionTimeout';

// Eagerly loaded (critical path)
import LoginPage from '@/pages/LoginPage';
import AcceptInvitePage from '@/pages/AcceptInvitePage';

// Lazy-loaded pages
const DashboardPage          = lazy(() => import('@/pages/DashboardPage'));
const RiskWorkflowWizard     = lazy(() => import('@/pages/RiskWorkflowWizard'));
const RisksPage              = lazy(() => import('@/pages/RisksPage'));
const ControlsPage           = lazy(() => import('@/pages/ControlsPage'));
const AuditsPage             = lazy(() => import('@/pages/AuditsPage'));
const FindingsPage           = lazy(() => import('@/pages/FindingsPage'));
const RemediationsPage       = lazy(() => import('@/pages/RemediationsPage'));
const FrameworksPage         = lazy(() => import('@/pages/FrameworksPage'));
const FrameworkDetailPage    = lazy(() => import('@/pages/FrameworkDetailPage'));
const ComplianceDashboardPage = lazy(() => import('@/pages/ComplianceDashboardPage'));
const AssessmentsPage        = lazy(() => import('@/pages/AssessmentsPage'));
const AssessmentDetailPage   = lazy(() => import('@/pages/AssessmentDetailPage'));
const TasksPage              = lazy(() => import('@/pages/TasksPage'));
const HelpPage               = lazy(() => import('@/pages/HelpPage'));
const ProfilePage            = lazy(() => import('@/pages/ProfilePage'));
const PoliciesPage           = lazy(() => import('@/pages/PoliciesPage'));
const ComplianceCalendarPage = lazy(() => import('@/pages/ComplianceCalendarPage'));
const ExercisesPage          = lazy(() => import('@/pages/ExercisesPage'));
const ExerciseDetailPage     = lazy(() => import('@/pages/ExerciseDetailPage'));
const M365ReportPage         = lazy(() => import('@/pages/M365ReportPage'));
const CyberAssessReportPage  = lazy(() => import('@/pages/CyberAssessReportPage'));
const UsersPage              = lazy(() => import('@/pages/admin/UsersPage'));
const BrandingPage           = lazy(() => import('@/pages/admin/BrandingPage'));
const FrameworksAdminPage    = lazy(() => import('@/pages/admin/FrameworksAdminPage'));
const FrameworkLibraryPage   = lazy(() => import('@/pages/admin/FrameworkLibraryPage'));
const SmtpPage               = lazy(() => import('@/pages/admin/SmtpPage'));
const AiSettingsPage         = lazy(() => import('@/pages/admin/AiSettingsPage'));
const SecuritySettingsPage   = lazy(() => import('@/pages/admin/SecuritySettingsPage'));
const AuditLogsPage          = lazy(() => import('@/pages/admin/AuditLogsPage'));
const SystemHealthPage       = lazy(() => import('@/pages/admin/SystemHealthPage'));
const SsoSettingsPage        = lazy(() => import('@/pages/admin/SsoSettingsPage'));
const EmailTemplatesPage     = lazy(() => import('@/pages/admin/EmailTemplatesPage'));
const NotificationSettingsPage = lazy(() => import('@/pages/admin/NotificationSettingsPage'));
const LookupsPage              = lazy(() => import('@/pages/admin/LookupsPage'));
const TrustCentreAdminPage     = lazy(() => import('@/pages/admin/TrustCentreAdminPage'));
const AdminSectionLayout       = lazy(() => import('@/components/layout/AdminSectionLayout'));
// The prospect-facing reader: no account, no app shell.
const TrustCentrePage          = lazy(() => import('@/pages/TrustCentrePage'));

// Client Assessment Portal — admin
const ClientsPage            = lazy(() => import('@/pages/admin/ClientsPage'));
const ClientDetailPage       = lazy(() => import('@/pages/admin/ClientDetailPage'));
const EngagementsPage        = lazy(() => import('@/pages/admin/EngagementsPage'));
const EngagementDetailPage   = lazy(() => import('@/pages/admin/EngagementDetailPage'));
const ClientAssessmentsPage  = lazy(() => import('@/pages/admin/ClientAssessmentsPage'));
const ClientExercisesPage    = lazy(() => import('@/pages/admin/ClientExercisesPage'));
const ClientUsersPage        = lazy(() => import('@/pages/admin/ClientUsersPage'));

// Client Assessment Portal — client portal
const PortalDashboardPage         = lazy(() => import('@/pages/portal/PortalDashboardPage'));
const PortalEngagementsPage       = lazy(() => import('@/pages/portal/PortalEngagementsPage'));
const PortalEngagementDetailPage  = lazy(() => import('@/pages/portal/PortalEngagementDetailPage'));
const PortalAssessmentDetailPage  = lazy(() => import('@/pages/portal/PortalAssessmentDetailPage'));
const PortalExercisesPage         = lazy(() => import('@/pages/portal/PortalExercisesPage'));
const PortalNotificationsPage     = lazy(() => import('@/pages/portal/PortalNotificationsPage'));
const PortalProfilePage           = lazy(() => import('@/pages/portal/PortalProfilePage'));
const PortalTrustCentrePage       = lazy(() => import('@/pages/portal/PortalTrustCentrePage'));
const PortalUsersPage             = lazy(() => import('@/pages/portal/PortalUsersPage'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
    </div>
  );
}

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function PrivateRoute() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  useSessionTimeout();
  if (!token) return <Navigate to="/login" replace />;
  // Client vs internal is decided from the JWT (authoritative + always fresh),
  // falling back to the persisted user only if the token cannot be decoded.
  // This prevents a stale/partial persisted profile from dropping a client user
  // into the internal admin shell.
  if (resolveClientOrgId(token, user?.clientOrgId)) return <Navigate to="/portal/dashboard" replace />;
  return (
    <SuspenseWrapper>
      <Layout />
    </SuspenseWrapper>
  );
}

function PortalRoute() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  useSessionTimeout();
  if (!token) return <Navigate to="/login" replace />;
  // Only client users can access portal — decided from the JWT.
  if (!resolveClientOrgId(token, user?.clientOrgId)) return <Navigate to="/dashboard" replace />;
  return (
    <SuspenseWrapper>
      <PortalLayout />
    </SuspenseWrapper>
  );
}

function AdminGuard() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  if (resolveRole(token, user?.role) !== 'ADMIN') return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

const router = createBrowserRouter([
  { path: '/login',          element: <LoginPage /> },
  { path: '/accept-invite',  element: <AcceptInvitePage /> },
  // Public by design: reached by a grant link, then gated by an emailed code
  // and the confidentiality terms. Deliberately outside PrivateRoute.
  { path: '/trust/:token',   element: <TrustCentrePage /> },
  {
    path: '/',
    element: <PrivateRoute />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard',         element: <DashboardPage /> },
      { path: 'risk-workflow',      element: <RiskWorkflowWizard /> },
      { path: 'risks',             element: <RisksPage /> },
      { path: 'controls',          element: <ControlsPage /> },
      { path: 'audits',            element: <AuditsPage /> },
      { path: 'findings',          element: <FindingsPage /> },
      { path: 'remediations',      element: <RemediationsPage /> },
      { path: 'frameworks',        element: <FrameworksPage /> },
      { path: 'frameworks/:id',    element: <FrameworkDetailPage /> },
      { path: 'compliance',        element: <ComplianceDashboardPage /> },
      { path: 'assessments',       element: <AssessmentsPage /> },
      { path: 'assessments/:id',   element: <AssessmentDetailPage /> },
      { path: 'assessments/:id/m365-report', element: <M365ReportPage /> },
      { path: 'assessments/:id/cyber-assess-report', element: <CyberAssessReportPage /> },
      { path: 'tasks',             element: <TasksPage /> },
      { path: 'policies',          element: <PoliciesPage /> },
      { path: 'calendar',          element: <ComplianceCalendarPage /> },
      { path: 'exercises',          element: <ExercisesPage /> },
      { path: 'trust-centre',       element: <TrustCentreAdminPage /> },
      { path: 'exercises/:id',      element: <ExerciseDetailPage /> },
      { path: 'help',              element: <HelpPage /> },
      { path: 'profile',           element: <ProfilePage /> },
      // Admin — role-guarded
      {
        path: 'admin',
        element: <AdminGuard />,
        children: [
          { index: true, element: <Navigate to="/admin/users" replace /> },
          // Settings pages, grouped: each group is one sidebar entry and the
          // pages in it are tabs (src/config/admin-nav.ts). URLs unchanged.
          {
            element: <AdminSectionLayout />,
            children: [
              { path: 'users',             element: <UsersPage /> },
              { path: 'branding',          element: <BrandingPage /> },
              { path: 'frameworks',        element: <FrameworksAdminPage /> },
              { path: 'framework-library', element: <FrameworkLibraryPage /> },
              { path: 'smtp',              element: <SmtpPage /> },
              { path: 'ai',                element: <AiSettingsPage /> },
              { path: 'security',          element: <SecuritySettingsPage /> },
              { path: 'audit-logs',        element: <AuditLogsPage /> },
              { path: 'health',            element: <SystemHealthPage /> },
              { path: 'sso',               element: <SsoSettingsPage /> },
              { path: 'email-templates',   element: <EmailTemplatesPage /> },
              { path: 'notifications',     element: <NotificationSettingsPage /> },
              { path: 'lookups',           element: <LookupsPage /> },
            ],
          },
          { path: 'clients',           element: <ClientsPage /> },
          { path: 'clients/:id',       element: <ClientDetailPage /> },
          { path: 'engagements',       element: <EngagementsPage /> },
          { path: 'engagements/:id',   element: <EngagementDetailPage /> },
          { path: 'client-assessments', element: <ClientAssessmentsPage /> },
          { path: 'client-exercises',   element: <ClientExercisesPage /> },
          { path: 'client-users',       element: <ClientUsersPage /> },
        ],
      },
    ],
  },
  // Client Assessment Portal
  {
    path: '/portal',
    element: <PortalRoute />,
    children: [
      { index: true, element: <Navigate to="/portal/dashboard" replace /> },
      { path: 'dashboard',           element: <PortalDashboardPage /> },
      { path: 'engagements',         element: <PortalEngagementsPage /> },
      { path: 'engagements/:id',         element: <PortalEngagementDetailPage /> },
      { path: 'engagements/:engagementId/assessments/:assessmentId', element: <PortalAssessmentDetailPage /> },
      { path: 'exercises',           element: <PortalExercisesPage /> },
      { path: 'notifications',       element: <PortalNotificationsPage /> },
      { path: 'trust-centre',        element: <PortalTrustCentrePage /> },
      { path: 'users',               element: <PortalUsersPage /> },
      { path: 'profile',             element: <PortalProfilePage /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

export default function App() {
  // Reconcile any stale persisted profile against the authoritative JWT once on
  // load, so client users with an out-of-date stored user are not misrouted.
  useEffect(() => {
    useAuthStore.getState().reconcileFromToken();
  }, []);
  return (
    <ErrorBoundary>
      <ToastContainer />
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}

